import { useCallback, useEffect, useState } from 'react'
import { governanceApi } from '../../services/api'
import { DataTable, type Column } from '../patterns'
import type { GovernanceActivityEvent } from './types'

const TYPE_TONE: Record<string, string> = {
  topic: 'bg-blue-500/15 text-blue-500',
  keyword: 'bg-amber-500/15 text-amber-500',
  barrier: 'bg-rose-500/15 text-rose-500',
}

// Mirrors the actionTaken literals written by lib/governance/events.ts +
// shell/jobs/governance-scan.ts. Unknown future values fall back to a
// de-slugged label + neutral tone rather than rendering nothing.
const ACTION_LABEL: Record<string, string> = {
  notified: 'Notified',
  hidden: 'Hidden',
  scan_failed: 'Scan failed',
  barrier_rejected: 'Blocked',
  keyword_rejected: 'Blocked',
}
const ACTION_TONE: Record<string, string> = {
  notified: 'bg-amber-500/15 text-amber-500',
  hidden: 'bg-rose-500/15 text-rose-500',
  scan_failed: 'bg-muted text-muted-foreground',
  barrier_rejected: 'bg-rose-500/15 text-rose-500',
  keyword_rejected: 'bg-rose-500/15 text-rose-500',
}

function truncate(s: string, n: number): string {
  if (!s) return ''
  return s.length > n ? `${s.slice(0, n)}…` : s
}

function Chip({ label, tone }: { label: string; tone: string }) {
  return (
    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>
      {label}
    </span>
  )
}

const PAGE_LIMIT = 50

/**
 * Read-only feed of governance rule matches (LogGovernanceEvent). Fetches its
 * own data — the caller only supplies scope (server, optionally one
 * department layer). Shared by the server-layer Policies tab (Task 8) and
 * the department-layer policy page (Task 9).
 */
export function ActivityTable({ serverId, departmentId }: { serverId: string; departmentId?: string }) {
  const [events, setEvents] = useState<GovernanceActivityEvent[]>([])
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!serverId) return
    setLoading(true)
    setError(null)
    try {
      const res = await governanceApi.activity(serverId, { departmentId, limit: PAGE_LIMIT })
      setEvents(res.data.events ?? [])
      setNextCursor(res.data.nextCursor ?? null)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load activity')
    } finally {
      setLoading(false)
    }
  }, [serverId, departmentId])

  useEffect(() => { load() }, [load])

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await governanceApi.activity(serverId, { departmentId, limit: PAGE_LIMIT, cursor: nextCursor })
      setEvents((prev) => [...prev, ...(res.data.events ?? [])])
      setNextCursor(res.data.nextCursor ?? null)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to load more activity')
    } finally {
      setLoadingMore(false)
    }
  }

  const columns: Column<GovernanceActivityEvent>[] = [
    {
      key: 'createdAt',
      header: 'Time',
      width: '160px',
      render: (e) => (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</span>
      ),
    },
    {
      key: 'ruleName',
      header: 'Rule',
      render: (e) => <span className="text-foreground">{e.ruleName || '—'}</span>,
    },
    {
      key: 'ruleType',
      header: 'Type',
      width: '90px',
      render: (e) => <Chip label={e.ruleType} tone={TYPE_TONE[e.ruleType] ?? 'bg-muted text-muted-foreground'} />,
    },
    {
      key: 'surface',
      header: 'Surface',
      width: '80px',
      render: (e) => <span className="capitalize text-muted-foreground">{e.surface}</span>,
    },
    {
      key: 'authorId',
      header: 'Author',
      width: '170px',
      render: (e) => (
        <span className="block truncate font-mono text-xs text-muted-foreground" title={e.authorId ?? undefined}>{e.authorId ?? '—'}</span>
      ),
    },
    {
      key: 'snippet',
      header: 'Snippet',
      render: (e) => <span className="text-foreground/90" title={e.snippet || undefined}>{truncate(e.snippet, 120) || '—'}</span>,
    },
    {
      key: 'actionTaken',
      header: 'Action',
      width: '110px',
      render: (e) => (
        <Chip label={ACTION_LABEL[e.actionTaken] ?? e.actionTaken.replace(/_/g, ' ')} tone={ACTION_TONE[e.actionTaken] ?? 'bg-muted text-muted-foreground'} />
      ),
    },
  ]

  if (loading) return <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>

  return (
    <div>
      {error && <div className="mx-4 mt-3 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
      <div className="overflow-x-auto">
        <DataTable
          columns={columns}
          rows={events}
          rowKey={(e) => e.id}
          empty={<div className="py-10 text-center text-sm text-muted-foreground">No policy activity yet.</div>}
        />
      </div>
      {nextCursor && (
        <div className="flex justify-center border-t border-border py-3">
          <button
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded bg-muted px-3 py-1.5 text-xs text-foreground transition hover:bg-accent disabled:opacity-50"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}
